import React, { useRef, useEffect } from 'react';

/**
 * Ortak pre + satır numarası componenti
 * Props:
 *  - value: gösterilecek metin
 *  - style: ek stil (opsiyonel)
 *  - preProps: <pre>'ye ekstra prop (opsiyonel)
 *  - lineNumbersRef: satır numarası için ref (opsiyonel)
 *  - preRef: pre için ref (opsiyonel)
 *  - onScroll: pre scroll event (opsiyonel)
 */
const defaultPreStyle = {
  width: '100%',
  height: '100%',
  border: '1px solid #d0d7de',
  borderRadius: '4px',
  fontFamily: 'Consolas, Monaco, monospace',
  fontSize: '14px',
  lineHeight: '1.5',
  boxSizing: 'border-box',
  margin: 0,
  boxShadow: 'inset 0 1px 2px rgba(27, 31, 35, 0.075)',
  padding: '10px 10px 10px 50px',
  position: 'relative',
  backgroundColor: '#fafafa',
  color: '#24292e',
  overflowY: 'auto',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  minHeight: '120px',
};

function renderLineNumbers(text, lineNumbersRef, className) {
  if (!text) return null;
  const lines = text.split('\n');
  const totalLines = lines.length;
  const lineNumWidth = totalLines > 999 ? 50 : totalLines > 99 ? 40 : 30;
  return (
    <div
      ref={lineNumbersRef}
      className={className}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${lineNumWidth}px`,
        textAlign: 'right',
        color: '#6e7681',
        userSelect: 'none',
        fontFamily: 'Consolas, Monaco, monospace',
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '1.5',
        height: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRight: '1px solid #e1e4e8',
        backgroundColor: '#f6f8fa',
        paddingTop: '10px',
        paddingBottom: '10px',
        zIndex: 1,
      }}
    >
      {lines.map((_, i) => (
        <div key={i} style={{
          lineHeight: '1.5',
          boxSizing: 'border-box',
          paddingRight: '10px',
          position: 'relative',
          color: (i + 1) % 5 === 0 ? '#24292e' : '#6e7681',
          fontWeight: (i + 1) % 10 === 0 ? '600' : '400',
          height: '21px',
        }}>{i + 1}</div>
      ))}
    </div>
  );
}

const PreWithLines = React.forwardRef(({
  value,
  style = {},
  preProps = {},
  lineNumbersRef,
  preRef,
  onScroll,
  lineNumbersRefClassName,
}, ref) => {
  const internalPreRef = useRef();
  useEffect(() => {
    if (preRef && internalPreRef.current) {
      if (typeof preRef === 'function') preRef(internalPreRef.current);
      else preRef.current = internalPreRef.current;
    }
    if (ref && internalPreRef.current) {
      if (typeof ref === 'function') ref(internalPreRef.current);
      else ref.current = internalPreRef.current;
    }
  }, [preRef, ref]);

  return (
    <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '120px' }}>
      {renderLineNumbers(value, lineNumbersRef, lineNumbersRefClassName)}
      <pre
        ref={internalPreRef}
        onScroll={onScroll}
        style={{ ...defaultPreStyle, ...style }}
        {...preProps}
      >
        {value || '\u200B'}
      </pre>
    </div>
  );
});

export default PreWithLines; 